import { FC, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';

import { formatTimeRange } from '@mopla/ui';

import { getRelativeDateLabel, getRelativeDay } from './utils';

export type TRideDateDisplay = 'date' | 'time' | 'timeToday';

export interface IRideDateLabelProps {
	startDate: Date | string;
	/** Required for the 'time' and 'timeToday' displays. */
	endDate?: Date | string;
	display?: TRideDateDisplay;
	/** Marks a rendered time as an estimate. Ignored when a date is shown. */
	isApproximate?: boolean;
	render?: (label: string) => ReactElement;
}

const defaultRender = (label: string): ReactElement => (
	<Typography variant="l1" color="primary">
		{label}
	</Typography>
);

export const RideDateLabel: FC<IRideDateLabelProps> = ({
	startDate,
	endDate,
	display = 'date',
	isApproximate,
	render = defaultRender,
}) => {
	const { t } = useTranslation('common');

	const label = useMemo(() => {
		const showTime =
			endDate &&
			(display === 'time' ||
				(display === 'timeToday' && getRelativeDay(startDate) === 'today'));

		if (!showTime) return getRelativeDateLabel(startDate, t);

		/** "Approximately" only makes sense in front of a time. */
		return `${isApproximate ? '~' : ''}${formatTimeRange(startDate, endDate)}`;
	}, [startDate, endDate, display, isApproximate, t]);

	return render(label);
};
